/**
 * The single rule this card is most likely to get wrong, encoded once with
 * its own tests: give is offered on active types only, take back is offered
 * on any type the member holds, active or archived.
 *
 * A type appears as a row in the give sheet if it is `status === "active"`,
 * OR the member holds at least one grant of it. A row is giveable only if
 * `status === "active"`. A row the member holds shows `Take back` in place
 * of the give control, and renders identically whether its type is active
 * or archived: no badge, no dimming, no label.
 *
 * `customAwardHistory` carries no `status` field, so the archived-ness of a
 * held type is known only by joining `awardTypeId` back into
 * `/award/custom-list`, and that join is used only to decide giveability,
 * never to change how a held row looks.
 */
import type { typeAwardKind } from "./poolAwardsCopy";
import type { typeAwardTypeRow } from "./poolAwardsModel";

export type typeGiveAwardRow = {
  awardTypeId: string;
  kind: typeAwardKind;
  name: string;
  color: string;
  /** How many grants of this type the member holds right now. */
  heldCount: number;
  /** The grant a Take back would revoke. null when heldCount is 0. */
  takeBackGrantId: string | null;
  /** False for an archived type. A held archived row still renders; it just cannot be given. */
  isGiveable: boolean;
};

export type typeGiveAwardModel = {
  rows: readonly typeGiveAwardRow[];
  medalRows: readonly typeGiveAwardRow[];
  trophyRows: readonly typeGiveAwardRow[];
  /** True when either kind has zero rows. See rule 6 in the header comment. */
  showKindLabels: boolean;
};

type typeHistoryEntry = {
  awardGrantId: string;
  awardTypeId: string;
  givenAt: string;
};

function rowsFor(
  kind: typeAwardKind,
  types: readonly typeAwardTypeRow[],
  history: readonly typeHistoryEntry[],
): typeGiveAwardRow[] {
  const rows: typeGiveAwardRow[] = [];
  for (const type of types) {
    const held = history.filter(
      (entry) => entry.awardTypeId === type.awardTypeId,
    );
    const heldCount = held.length;
    if (type.status !== "active" && heldCount === 0) continue;

    let takeBackGrantId: string | null = null;
    if (held.length > 0) {
      const newest = held.reduce((latest, entry) =>
        entry.givenAt >= latest.givenAt ? entry : latest,
      );
      takeBackGrantId = newest.awardGrantId;
    }

    rows.push({
      awardTypeId: type.awardTypeId,
      kind,
      name: type.name,
      color: type.color,
      heldCount,
      takeBackGrantId,
      isGiveable: type.status === "active",
    });
  }
  return rows;
}

export function buildGiveAwardModel(input: {
  medals: readonly typeAwardTypeRow[];
  trophies: readonly typeAwardTypeRow[];
  history: readonly typeHistoryEntry[];
}): typeGiveAwardModel {
  const medalRows = rowsFor("medal", input.medals, input.history);
  const trophyRows = rowsFor("trophy", input.trophies, input.history);

  return {
    rows: [...medalRows, ...trophyRows],
    medalRows,
    trophyRows,
    showKindLabels: medalRows.length === 0 || trophyRows.length === 0,
  };
}
